import { useEffect } from 'react';

const DEFAULT_SITE_NAME = 'Локомотив';
const DEFAULT_DESCRIPTION =
    'Интерактивная web-платформа для моделирования и управления железнодорожной инфраструктурой в реальном времени.';

const ROBOTS_INDEX = 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1';
const ROBOTS_NOINDEX = 'noindex,nofollow,noarchive';

export interface SeoOptions {
    title: string;
    description?: string;
    canonicalPath?: string;
    robots?: string;
    image?: string;
    structuredData?: Record<string, unknown> | Record<string, unknown>[];
}

const getSiteOrigin = () => {
    const envOrigin = import.meta.env.VITE_SITE_URL?.trim();

    if (envOrigin) {
        return envOrigin.replace(/\/$/, '');
    }

    if (typeof window !== 'undefined' && window.location.origin) {
        return window.location.origin;
    }

    return 'http://localhost:3000';
};

const toAbsoluteUrl = (value: string) => {
    if (/^https?:\/\//i.test(value)) {
        return value;
    }

    const normalizedValue = value.startsWith('/') ? value : `/${value}`;
    return new URL(normalizedValue, getSiteOrigin()).toString();
};

const upsertMeta = (attrName: 'name' | 'property', attrValue: string, content: string) => {
    const selector = `meta[${attrName}="${attrValue}"]`;
    const existing = document.head.querySelector<HTMLMetaElement>(selector);

    if (existing) {
        existing.setAttribute('content', content);
        return;
    }

    const meta = document.createElement('meta');
    meta.setAttribute(attrName, attrValue);
    meta.setAttribute('content', content);
    document.head.appendChild(meta);
};

const setNamedMeta = (name: string, content: string) => {
    upsertMeta('name', name, content);
};

const setPropertyMeta = (property: string, content: string) => {
    upsertMeta('property', property, content);
};

const removeMeta = (attrName: 'name' | 'property', attrValue: string) => {
    const existing = document.head.querySelector<HTMLMetaElement>(`meta[${attrName}="${attrValue}"]`);
    existing?.remove();
};

const setLink = (rel: string, href: string) => {
    const existing = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);

    if (existing) {
        existing.setAttribute('href', href);
        return;
    }

    const link = document.createElement('link');
    link.rel = rel;
    link.href = href;
    document.head.appendChild(link);
};

const setStructuredData = (value?: Record<string, unknown> | Record<string, unknown>[]) => {
    const scriptId = 'seo-structured-data';
    const existing = document.getElementById(scriptId);

    if (!value) {
        existing?.remove();
        return;
    }

    const payload = Array.isArray(value) ? value : [value];
    const json = JSON.stringify(payload.length === 1 ? payload[0] : payload);

    if (existing) {
        existing.textContent = json;
        return;
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';
    script.textContent = json;
    document.head.appendChild(script);
};

export const useSeo = ({
    title,
    description = DEFAULT_DESCRIPTION,
    canonicalPath = '/',
    robots = ROBOTS_INDEX,
    image = '/social-preview.jpg',
    structuredData,
}: SeoOptions) => {
    useEffect(() => {
        if (typeof document === 'undefined') {
            return;
        }

        const canonicalUrl = toAbsoluteUrl(canonicalPath);
        const absoluteImage = image ? toAbsoluteUrl(image) : undefined;
        const fullTitle = title === DEFAULT_SITE_NAME ? title : `${title} | ${DEFAULT_SITE_NAME}`;

        document.title = fullTitle;
        setNamedMeta('description', description);
        setNamedMeta('robots', robots);
        setPropertyMeta('og:title', fullTitle);
        setPropertyMeta('og:description', description);
        setPropertyMeta('og:type', 'website');
        setPropertyMeta('og:url', canonicalUrl);
        setPropertyMeta('og:site_name', DEFAULT_SITE_NAME);
        setNamedMeta('twitter:card', 'summary_large_image');
        setNamedMeta('twitter:title', fullTitle);
        setNamedMeta('twitter:description', description);

        if (absoluteImage) {
            setPropertyMeta('og:image', absoluteImage);
            setNamedMeta('twitter:image', absoluteImage);
        } else {
            removeMeta('property', 'og:image');
            removeMeta('name', 'twitter:image');
        }

        setLink('canonical', canonicalUrl);
        setStructuredData(structuredData ?? (canonicalPath === '/' ? { '@context': 'https://schema.org', '@type': 'WebSite', name: DEFAULT_SITE_NAME, url: canonicalUrl, description, inLanguage: 'ru-RU' } : undefined));
    }, [title, description, canonicalPath, robots, image, structuredData]);
};

export const seoDefaults = {
    siteName: DEFAULT_SITE_NAME,
    description: DEFAULT_DESCRIPTION,
    robots: {
        index: ROBOTS_INDEX,
        noindex: ROBOTS_NOINDEX,
    },
};
